/**
 * ActivityAttributesConfigurable Component
 *
 * Rule-based configurable attribute editing component
 * Replaces ActivityAttributesAlwaysVisible with intelligent attribute handling
 */

import React, { useMemo, useEffect, useImperativeHandle, forwardRef } from "react"
import { VStack, Text, SimpleGrid } from "@chakra-ui/react"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { AttributeField, IoTAttributeField } from "./attributes"
import type { LinkedActivityInfo, AttributeFieldRef } from "./attributes/AttributeField"
import { useAttributeRuleEngine } from "@hydrologiq/shared/utils/attribute-rules"
import type { ActivityContext } from "@hydrologiq/shared/utils/attribute-rules"

export interface AttributeValues {
  pressure: string
  quantity: string
  temperature: string
  fill_level: string
  asset: string | null
  site: string | null
  batch: string | null
  source: string | null
  destination: string | null
  note: string
  [key: string]: string | null
}

export interface ActivityAttributesConfigurableProps {
  order?: gqlTypes.Order
  asset?: gqlTypes.Asset
  availableAssets?: gqlTypes.AssetBasic[]
  activities: gqlTypes.Activity[]
  activity: gqlTypes.Activity
  eventKind: gqlTypes.ActivityEventKind
  values: AttributeValues
  onChange: (values: AttributeValues | ((prev: AttributeValues) => AttributeValues)) => void
  refreshTrigger?: number
  onNavigateToBatchCreation?: () => void
  newlyCreatedBatch?: { id: string; name: string } | null // Batch that was just created
  allowAnyAsset?: boolean
}

export interface ActivityAttributesConfigurableRef {
  validateAll: () => boolean
}

/**
 * Configurable attributes component using rule engine
 *
 * Automatically shows/hides/disables fields based on activity type and status
 */
export const ActivityAttributesConfigurable = forwardRef<
  ActivityAttributesConfigurableRef,
  ActivityAttributesConfigurableProps
>(
  (
    {
      order,
      asset,
      availableAssets,
      activities,
      activity,
      eventKind,
      values,
      onChange,
      onNavigateToBatchCreation,
      newlyCreatedBatch,
      allowAnyAsset,
    },
    ref
  ) => {
    const ruleEngine = useAttributeRuleEngine()

    // Track which calculated fields have been manually overridden by the user
    // Key: attribute key, Value: true if user has manually edited
    const [userOverrides, setUserOverrides] = React.useState<Record<string, boolean>>({})

    // Store refs to all attribute fields
    const fieldRefs = React.useRef<Map<string, AttributeFieldRef>>(new Map())

    // Expose validation method
    useImperativeHandle(ref, () => ({
      validateAll: () => {
        let allValid = true
        fieldRefs.current.forEach((fieldRef) => {
          const result = fieldRef.validate()
          if (result && !result.valid) {
            allValid = false
          }
        })
        return allValid
      },
    }))

    // Build activity context for rule evaluation
    // IMPORTANT: Only recreate context when order/activity/eventKind changes
    // NOT when values change - this prevents unnecessary IoT refetches and re-renders
    const baseContext: ActivityContext = useMemo(
      () => ({
        order,
        asset,
        availableAssets,
        activity,
        eventKind,
        activities,
        allowAnyAsset,
      }),
      [order, activity, eventKind, activities, availableAssets, allowAnyAsset]
    )

    // Create context with current values for calculator/validator functions
    // This is recreated on every render but that's OK - it's just used for calculations
    const contextWithValues: ActivityContext = useMemo(
      () => ({
        ...baseContext,
        currentValues: values,
      }),
      [baseContext, values]
    )

    // Map attribute kind to values key (moved up before use in visibleConfigs)
    const getAttributeKey = (attribute: gqlTypes.ActivityAttributeKind): string => {
      const mapping: Record<gqlTypes.ActivityAttributeKind, string> = {
        [gqlTypes.ActivityAttributeKind.Pressure]: "pressure",
        [gqlTypes.ActivityAttributeKind.Quantity]: "quantity",
        [gqlTypes.ActivityAttributeKind.Temperature]: "temperature",
        [gqlTypes.ActivityAttributeKind.Site]: "site",
        [gqlTypes.ActivityAttributeKind.Batch]: "batch",
        [gqlTypes.ActivityAttributeKind.FillLevel]: "fill_level",
        [gqlTypes.ActivityAttributeKind.Note]: "note",
        [gqlTypes.ActivityAttributeKind.Source]: "source",
        [gqlTypes.ActivityAttributeKind.Destination]: "destination",
      }
      return mapping[attribute] || attribute
    }

    // Get visible attributes for current activity type and status, sorted by order
    const visibleConfigs = useMemo(() => {
      const configs = ruleEngine.getVisibleAttributes(activity.kind, eventKind)
      // Sort by order (attributes without order go first, then by order number)
      return configs.sort((a, b) => {
        const orderA = a.order ?? 0
        const orderB = b.order ?? 0
        return orderA - orderB
      })
    }, [ruleEngine, activity.kind, eventKind])

    // Get ALL attributes (including hidden) for data processing
    const allConfigs = useMemo(() => {
      return ruleEngine.getAllAttributes(activity.kind, eventKind)
    }, [ruleEngine, activity.kind, eventKind])

    // Auto-populate hidden attributes with default values ONCE on mount
    // This should NOT run on every state change or context change
    useEffect(() => {
      const hiddenConfigs = allConfigs.filter((c) => c.visibility === "hidden")

      if (hiddenConfigs.length === 0) return

      // Build updates in batch to avoid multiple onChange calls
      const updates: Record<string, string> = {}

      hiddenConfigs.forEach((config) => {
        const attributeKey = getAttributeKey(config.attribute as gqlTypes.ActivityAttributeKind)

        // Only set if has default value and current value is empty
        if (config.defaultValue && !values[attributeKey]) {
          const defaultVal = config.defaultValue({
            ...baseContext,
            currentValues: values,
          } as ActivityContext)

          if (defaultVal) {
            updates[attributeKey] = defaultVal
          }
        }
      })

      // Apply all updates in a single onChange call
      if (Object.keys(updates).length > 0) {
        onChange((prevValues) => ({
          ...prevValues,
          ...updates,
        }))
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Run ONLY on mount, not on any changes

    // Track dependency values to detect when they change (to reset overrides)
    const dependencyValuesRef = React.useRef<Record<string, string>>({})

    // Auto-run calculators when dependencies change
    useEffect(() => {
      const calculatorConfigs = allConfigs.filter((config) => config.calculator)
      if (calculatorConfigs.length === 0) return

      // Debounce calculator execution to avoid interfering with user input
      const timeoutId = setTimeout(async () => {
        const freshContext = {
          ...baseContext,
          currentValues: values,
        } as ActivityContext

        const newValues: Record<string, string> = {}
        const clearedOverrides: string[] = []
        let hasChanges = false

        for (const config of calculatorConfigs) {
          const attributeKey = getAttributeKey(config.attribute as gqlTypes.ActivityAttributeKind)
          const currentValue = values[attributeKey]

          // Check if any dependencies have changed
          const dependencies = config.calculatorDependencies || []
          const dependenciesChanged = dependencies.some((dep) => {
            const depKey = getAttributeKey(dep)
            const oldValue = dependencyValuesRef.current[depKey]
            const newValue = values[depKey]
            return oldValue !== newValue
          })

          // If dependencies changed, clear the override flag for this attribute
          if (dependenciesChanged && userOverrides[attributeKey]) {
            clearedOverrides.push(attributeKey)
          }

          // Only auto-calculate if user hasn't manually overridden this field
          // OR if dependencies just changed (which clears the override)
          const shouldCalculate = !userOverrides[attributeKey] || dependenciesChanged

          if (!shouldCalculate) {
            continue
          }

          try {
            const calculatedValue = await ruleEngine.calculateValue(config, freshContext)

            if (calculatedValue && calculatedValue !== currentValue) {
              newValues[attributeKey] = calculatedValue
              hasChanges = true
            } else if (!calculatedValue && currentValue && config.attribute === "fill_level") {
              // Only reset fill_level if P or T is empty AND there's an existing saved value
              // Don't reset for new activities where user is manually entering values
              const originalFillLevel = activity.attributes?.find(
                (a) => a.attribute === "fill_level" && a.event_kind === eventKind
              )?.value
              // Only reset if there was an original value and it differs from current
              if (originalFillLevel && originalFillLevel !== currentValue) {
                newValues[attributeKey] = originalFillLevel
                hasChanges = true
              }
            }
          } catch (err) {}
        }

        // Update dependency tracking
        allConfigs
          .filter((c) => c.calculator && c.calculatorDependencies)
          .flatMap((c) => c.calculatorDependencies || [])
          .forEach((dep) => {
            const depKey = getAttributeKey(dep)
            dependencyValuesRef.current[depKey] = values[depKey] || ""
          })

        // Clear override flags for attributes whose dependencies changed
        if (clearedOverrides.length > 0) {
          setUserOverrides((prev) => {
            const updated = { ...prev }
            clearedOverrides.forEach((key) => {
              delete updated[key]
            })
            return updated
          })
        }

        if (hasChanges) {
          onChange((prevValues) => ({ ...prevValues, ...newValues }))
        }
      }, 250)

      return () => clearTimeout(timeoutId)
    }, [
      allConfigs,
      order,
      activity,
      eventKind,
      ruleEngine,
      baseContext,
      userOverrides,
      // Watch all attributes that are calculator dependencies
      ...allConfigs
        .filter((c) => c.calculator && c.calculatorDependencies)
        .flatMap((c) => c.calculatorDependencies || [])
        .map((dep) => values[getAttributeKey(dep)]),
    ])

    // Handle individual attribute change
    const handleAttributeChange = (attributeKey: string, value: string) => {
      // Check if this is a calculated field
      const isCalculatedField = allConfigs.some(
        (config) =>
          config.calculator && getAttributeKey(config.attribute as gqlTypes.ActivityAttributeKind) === attributeKey
      )

      // If user is manually editing a calculated field, mark it as overridden
      if (isCalculatedField) {
        setUserOverrides((prev) => ({
          ...prev,
          [attributeKey]: true,
        }))
      }

      // Use functional update to avoid stale state when multiple changes happen quickly
      onChange((prevValues) => {
        // Skip update if value hasn't actually changed
        if (prevValues[attributeKey] === value) {
          return prevValues
        }

        return {
          ...prevValues,
          [attributeKey]: value,
        }
      })
    }

    // Sort configs by order (lower numbers first)
    const sortedConfigs = useMemo(() => {
      return [...visibleConfigs].sort((a, b) => {
        const orderA = a.order ?? 999
        const orderB = b.order ?? 999
        return orderA - orderB
      })
    }, [visibleConfigs])

    // Update the base context with the selected asset when the attribute value changes
    useEffect(() => {
      if (values.asset) {
        baseContext.selectedAsset = values.asset
      }
    }, [values.asset])

    // Helper function to determine if an attribute is inherited from a linked activity
    const getLinkedActivityInfo = (attribute: gqlTypes.ActivityAttributeKind): LinkedActivityInfo | undefined => {
      // Only check for site-related attributes
      if (
        ![
          gqlTypes.ActivityAttributeKind.Site,
          gqlTypes.ActivityAttributeKind.Source,
          gqlTypes.ActivityAttributeKind.Destination,
          gqlTypes.ActivityAttributeKind.Quantity,
        ].includes(attribute)
      ) {
        return undefined
      }

      const attributeKey = getAttributeKey(attribute)
      const currentValue = values[attributeKey]

      if (!currentValue) return undefined

      // Check if this attribute has a value and comes from a linked activity
      // Source attribute can be inherited from source_links
      if (
        attribute === gqlTypes.ActivityAttributeKind.Source &&
        activity.source_links &&
        activity.source_links.length > 0
      ) {
        const sourceLink = activity.source_links[0]
        // Check if the current source value matches any attribute from the linked activity
        const currentAttr = activity.attributes?.find(
          (a) =>
            a.attribute === gqlTypes.ActivityAttributeKind.Source &&
            a.event_kind === eventKind &&
            a.site?.id === currentValue
        )
        if (currentAttr) {
          return {
            activityName: sourceLink.activity_name || sourceLink.activity_id,
            activityKind: sourceLink.activity_kind || "unknown",
            isInherited: true,
          }
        }
      }

      // Destination attribute can be inherited from target_links
      if (
        attribute === gqlTypes.ActivityAttributeKind.Destination &&
        activity.target_links &&
        activity.target_links.length > 0
      ) {
        const targetLink = activity.target_links[0]
        const currentAttr = activity.attributes?.find(
          (a) =>
            a.attribute === gqlTypes.ActivityAttributeKind.Destination &&
            a.event_kind === eventKind &&
            a.site?.id === currentValue
        )
        if (currentAttr) {
          return {
            activityName: targetLink.activity_name || targetLink.activity_id,
            activityKind: targetLink.activity_kind || "unknown",
            isInherited: true,
          }
        }
      }

      // Site attribute can be inherited from either source or target links
      const bothAttrs = [gqlTypes.ActivityAttributeKind.Site, gqlTypes.ActivityAttributeKind.Quantity]
      if (bothAttrs.includes(attribute)) {
        const currentAttr = activity.attributes?.find(
          (a) =>
            bothAttrs.includes(a.attribute as gqlTypes.ActivityAttributeKind) &&
            a.event_kind === eventKind &&
            a.site?.id === currentValue
        )
        if (currentAttr) {
          // Check source links first
          if (activity.source_links && activity.source_links.length > 0) {
            const sourceLink = activity.source_links[0]
            return {
              activityName: sourceLink.activity_name || sourceLink.activity_id,
              activityKind: sourceLink.activity_kind || "unknown",
              isInherited: true,
            }
          }
          // Check target links
          if (activity.target_links && activity.target_links.length > 0) {
            const targetLink = activity.target_links[0]
            return {
              activityName: targetLink.activity_name || targetLink.activity_id,
              activityKind: targetLink.activity_kind || "unknown",
              isInherited: true,
            }
          }
        }
      }

      return undefined
    }

    return (
      <VStack gap={3} align="stretch" width="100%">
        {visibleConfigs.length === 0 && (
          <Text fontSize="sm" color="fg.muted">
            No attributes required for this status
          </Text>
        )}

        <SimpleGrid columns={2} gap={3} px={0}>
          {sortedConfigs.map((config) => {
            const attributeKey =
              config.attribute === "asset"
                ? "asset"
                : getAttributeKey(config.attribute as gqlTypes.ActivityAttributeKind)

            // Use form value directly
            // For site/batch fields, preserve null (means "no value, apply defaults")
            // For other fields, use empty string as default
            const isSiteOrBatch = [
              gqlTypes.ActivityAttributeKind.Site,
              gqlTypes.ActivityAttributeKind.Batch,
              gqlTypes.ActivityAttributeKind.Source,
              gqlTypes.ActivityAttributeKind.Destination,
            ].includes(config.attribute as gqlTypes.ActivityAttributeKind)
            const displayValue = isSiteOrBatch ? (values[attributeKey] ?? null) : (values[attributeKey] ?? "")

            // Create a key that includes activity kind for site fields to force remount when switching
            // between filling and non-filling activities (they have different site types and defaults)
            const fieldKey = [
              gqlTypes.ActivityAttributeKind.Site,
              gqlTypes.ActivityAttributeKind.Batch,
              gqlTypes.ActivityAttributeKind.Source,
              gqlTypes.ActivityAttributeKind.Destination,
            ].includes(config.attribute as gqlTypes.ActivityAttributeKind)
              ? `${config.attribute}-${activity.kind}-${order?.id}`
              : config.attribute === "asset"
              ? availableAssets?.map((a) => a.id).join(", ")
              : `${config.attribute}-${order?.id}`

            // Render IoT or standard field
            // Use contextWithValues for all fields so calculators have access to current values
            if (config.visibility === "iot" && config.iotSource) {
              return (
                <IoTAttributeField
                  key={fieldKey}
                  config={config}
                  context={contextWithValues}
                  value={displayValue}
                  onChange={(newValue) => handleAttributeChange(attributeKey, newValue)}
                />
              )
            } else {
              return (
                <AttributeField
                  key={fieldKey}
                  ref={(el) => {
                    if (el) {
                      fieldRefs.current.set(attributeKey, el)
                    } else {
                      fieldRefs.current.delete(attributeKey)
                    }
                  }}
                  config={config}
                  context={contextWithValues}
                  value={displayValue}
                  onChange={(newValue) => handleAttributeChange(attributeKey, newValue)}
                  onNavigateToBatchCreation={onNavigateToBatchCreation}
                  newlyCreatedBatch={
                    config.attribute === gqlTypes.ActivityAttributeKind.Batch ? newlyCreatedBatch : undefined
                  }
                  linkedActivityInfo={getLinkedActivityInfo(config.attribute as gqlTypes.ActivityAttributeKind)}
                />
              )
            }
          })}
        </SimpleGrid>
      </VStack>
    )
  }
)

// Export memoized version to prevent unnecessary re-renders
export default React.memo(ActivityAttributesConfigurable)
